/**
 * Office-simulation engine for the Visualize page. Pure TypeScript — no React,
 * no DOM. The agent-ui server owns the single authoritative instance (ticked
 * every 250ms, inputs refreshed every 2s); browsers render snapshots.
 *
 * Modes:
 *   working    — has a running spawn → hurries to a desk and types
 *   visiting   — messaged a persona who's working → stands at their desk
 *   meeting    — two idle personas messaging → chat in the meeting room
 *   wandering  — spawned recently → strolls office + garden points of interest
 *   resting    — hasn't spawned in a while → naps (couch / garden bench, capped)
 *   leaving    — hasn't spawned in ages → walks out the door
 *   away       — off-screen; re-enters through the door on the next spawn
 *
 * Isolated personas (bus `isolated_agents`, e.g. vulcan) live in the server
 * room: they work, wander and rest inside it.
 *
 * Movement follows A*-found paths on a 16px grid (no walking through desks).
 * Positions mutate every tick; `version` bumps only on render-worthy changes.
 */

export const STAGE = { w: 1180, h: 640 };
export const DOOR = { x: 232, y: 82 };

// ── Floor plan ───────────────────────────────────────────────────────────────
// Interior x 0..900 (wall-backed), garden x 900..1180. Rooms are glass boxes
// with door gaps. All rects are [x, y, w, h].

export const WALL_H = 56;
export const INTERIOR_W = 900;

// Desks: two rows of four in the middle work area.
export const DESKS = (() => {
  const out: Array<{ x: number; y: number }> = [];
  const cols = 4, x0 = 300, dx = 130, rows = [150, 290];
  for (const y of rows) for (let c = 0; c < cols; c++) out.push({ x: x0 + c * dx, y });
  return out;
})();

// Server room (top-right interior) — isolated personas live here.
export const SERVER_ROOM = { x: 740, y: 60, w: 154, h: 140, doorX: 795, doorW: 45 }; // door on bottom wall
export const SERVER_DESKS = [{ x: 790, y: 165 }, { x: 855, y: 165 }];
const SERVER_POIS: POI[] = [
  { x: 775, y: 125, action: "🔧 tending the racks" },
  { x: 865, y: 125, action: "📟 watching logs" },
  { x: 820, y: 160, action: null },
];
const SERVER_REST = { x: 865, y: 160 };
const SERVER_DOOR_SPOT = { x: 818, y: 215 }; // just outside the door

// Meeting room (bottom-right interior) — pairs chat here.
export const MEETING_ROOM = { x: 620, y: 470, w: 270, h: 160, doorX: 725, doorW: 50 }; // door on top wall
export const MEETING_TABLE = { x: 700, y: 560, w: 120, h: 40 };
const MEET_SPOTS: Array<{ x: number; y: number }> = [
  { x: 730, y: 552 }, { x: 800, y: 552 }, { x: 758, y: 620 }, { x: 680, y: 610 },
];

// Pantry (bottom-left interior).
export const PANTRY = { x: 24, y: 480, w: 280, h: 150 };
export const PANTRY_TABLE = { x: 80, y: 540, w: 160, h: 30 };

// Garden (right strip, outdoors). Fence at x=900 with a gate.
export const GARDEN_X = 900;
export const GATE = { y: 300, h: 70 };
export const POND = { x: 945, y: 265, w: 110, h: 70 };
export const TREES = [{ x: 940, y: 120 }, { x: 1105, y: 90 }, { x: 1060, y: 540 }, { x: 930, y: 590 }];
export const BENCHES = [{ x: 935, y: 470 }, { x: 1095, y: 410 }];

interface POI { x: number; y: number; action: string | null }

const OFFICE_POIS: POI[] = [
  { x: 60, y: 545, action: "☕ coffee break" },
  { x: 268, y: 545, action: "🥪 grabbing a snack" },
  { x: 120, y: 590, action: "🍜 at the big table" },
  { x: 200, y: 590, action: "🍜 at the big table" },
  { x: 106, y: 92, action: "📋 checking the board" },
  { x: 352, y: 90, action: "🌆 looking outside" },
  { x: 502, y: 90, action: "🌆 looking outside" },
  { x: 652, y: 90, action: "🌆 looking outside" },
  { x: 24, y: 112, action: "🪴 admiring the plant" },
  { x: 264, y: 112, action: "🪴 admiring the plant" },
  { x: 70, y: 160, action: null }, { x: 140, y: 220, action: null },
  { x: 55, y: 280, action: null }, { x: 150, y: 330, action: null },
  { x: 350, y: 235, action: null }, { x: 480, y: 240, action: null },
  { x: 610, y: 235, action: null }, { x: 480, y: 400, action: null },
  { x: 300, y: 420, action: null }, { x: 160, y: 430, action: null },
];

const GARDEN_POIS: POI[] = [
  { x: 1000, y: 355, action: "🪷 watching the pond" },
  { x: 935, y: 220, action: "🌸 smelling the flowers" },
  { x: 1130, y: 250, action: "🌸 smelling the flowers" },
  { x: 1035, y: 480, action: null },
  { x: 1120, y: 180, action: null },
  { x: 960, y: 560, action: null },
];

// Lunch-rush destinations — wanderers flock here 12:00–13:00 WITA.
const LUNCH_POIS: POI[] = [
  { x: 100, y: 588, action: "🍜 lunch" },
  { x: 140, y: 588, action: "🍜 lunch" },
  { x: 180, y: 588, action: "🍜 lunch" },
  { x: 220, y: 588, action: "🍜 lunch" },
  { x: 60, y: 545, action: "☕ coffee break" },
  { x: 268, y: 545, action: "🥪 grabbing a snack" },
];

// Where the office cat likes to be. It ignores pathfinding — cats walk over
// desks, that's the point.
const CAT_SPOTS: Array<{ x: number; y: number; action: string | null }> = [
  ...DESKS.map((d) => ({ x: d.x, y: d.y + 34, action: "🐾 on the desk" })),
  { x: 82, y: 132, action: "💤" },                       // couch
  { x: 1000, y: 350, action: "🐟 watching the fish" },   // pond
  { x: 160, y: 585, action: "🐾 begging for food" },     // pantry table
  { x: 480, y: 400, action: null }, { x: 300, y: 240, action: null },
  { x: 700, y: 420, action: null }, { x: 1050, y: 500, action: null },
  { x: 950, y: 180, action: null },
];

export interface CatState {
  x: number;
  y: number;
  face: 1 | -1;
  path: Array<{ x: number; y: number }>;
  action: string | null;
  pending: string | null;
  speed: number;
}

// Nap spots — couch seats, quiet corners, garden benches. Resting is capped to
// this list; anyone sleepier than the last free spot goes home instead.
const REST_SPOTS: Array<{ x: number; y: number }> = [
  { x: 58, y: 148 }, { x: 96, y: 148 },                       // couch
  { x: 947, y: 468 }, { x: 981, y: 468 },                     // garden bench 1
  { x: 1107, y: 408 }, { x: 1141, y: 408 },                   // garden bench 2
  { x: 28, y: 330 }, { x: 205, y: 215 },                      // rug corners
];

// ── Pathfinding: A* on a 16px grid ──────────────────────────────────────────

const CELL = 16;
const GRID_W = Math.ceil(STAGE.w / CELL);
const GRID_H = Math.ceil(STAGE.h / CELL);

type Rect = { x: number; y: number; w: number; h: number };

const OBSTACLES: Rect[] = [
  { x: 0, y: 0, w: INTERIOR_W, h: WALL_H },                                    // back wall
  ...DESKS.map((d) => ({ x: d.x - 34, y: d.y + 20, w: 68, h: 30 })),           // desks
  { x: 40, y: 120, w: 84, h: 30 },                                             // couch
  // server room walls (glass), door gap on the bottom edge
  { x: SERVER_ROOM.x, y: SERVER_ROOM.y, w: SERVER_ROOM.w, h: 6 },
  { x: SERVER_ROOM.x, y: SERVER_ROOM.y, w: 6, h: SERVER_ROOM.h },
  { x: SERVER_ROOM.x + SERVER_ROOM.w - 6, y: SERVER_ROOM.y, w: 6, h: SERVER_ROOM.h },
  { x: SERVER_ROOM.x, y: SERVER_ROOM.y + SERVER_ROOM.h - 6, w: SERVER_ROOM.doorX - SERVER_ROOM.x, h: 6 },
  { x: SERVER_ROOM.doorX + SERVER_ROOM.doorW, y: SERVER_ROOM.y + SERVER_ROOM.h - 6, w: SERVER_ROOM.x + SERVER_ROOM.w - SERVER_ROOM.doorX - SERVER_ROOM.doorW, h: 6 },
  { x: 750, y: 70, w: 30, h: 34 }, { x: 850, y: 70, w: 30, h: 34 },            // racks
  // meeting room walls (glass), door gap on the top edge
  { x: MEETING_ROOM.x, y: MEETING_ROOM.y, w: MEETING_ROOM.doorX - MEETING_ROOM.x, h: 6 },
  { x: MEETING_ROOM.doorX + MEETING_ROOM.doorW, y: MEETING_ROOM.y, w: MEETING_ROOM.x + MEETING_ROOM.w - MEETING_ROOM.doorX - MEETING_ROOM.doorW, h: 6 },
  { x: MEETING_ROOM.x, y: MEETING_ROOM.y, w: 6, h: MEETING_ROOM.h },
  { x: MEETING_ROOM.x + MEETING_ROOM.w - 6, y: MEETING_ROOM.y, w: 6, h: MEETING_ROOM.h },
  { x: MEETING_TABLE.x, y: MEETING_TABLE.y, w: MEETING_TABLE.w, h: MEETING_TABLE.h },
  { x: PANTRY_TABLE.x, y: PANTRY_TABLE.y, w: PANTRY_TABLE.w, h: PANTRY_TABLE.h },
  { x: 40, y: 500, w: 40, h: 24 },                                             // coffee machine counter
  { x: 256, y: 495, w: 32, h: 36 },                                            // fridge
  { x: POND.x, y: POND.y, w: POND.w, h: POND.h },                              // pond
  // fence with a gate
  { x: GARDEN_X - 4, y: WALL_H, w: 8, h: GATE.y - WALL_H },
  { x: GARDEN_X - 4, y: GATE.y + GATE.h, w: 8, h: STAGE.h - GATE.y - GATE.h },
  ...TREES.map((t) => ({ x: t.x - 12, y: t.y - 8, w: 24, h: 20 })),            // tree trunks
];

const blocked: Uint8Array = (() => {
  const g = new Uint8Array(GRID_W * GRID_H);
  for (const r of OBSTACLES) {
    const x0 = Math.max(0, Math.floor(r.x / CELL)), x1 = Math.min(GRID_W - 1, Math.floor((r.x + r.w - 1) / CELL));
    const y0 = Math.max(0, Math.floor(r.y / CELL)), y1 = Math.min(GRID_H - 1, Math.floor((r.y + r.h - 1) / CELL));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) g[y * GRID_W + x] = 1;
  }
  return g;
})();

const cellOf = (p: { x: number; y: number }) => ({
  cx: Math.min(GRID_W - 1, Math.max(0, Math.floor(p.x / CELL))),
  cy: Math.min(GRID_H - 1, Math.max(0, Math.floor(p.y / CELL))),
});
const isBlocked = (cx: number, cy: number) => blocked[cy * GRID_W + cx] === 1;

/** Nudge a cell to the nearest free cell (spots sit close to furniture). */
function freeCellNear(cx: number, cy: number): { cx: number; cy: number } | null {
  if (!isBlocked(cx, cy)) return { cx, cy };
  for (let r = 1; r <= 4; r++) {
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= GRID_W || ny >= GRID_H) continue;
      if (!isBlocked(nx, ny)) return { cx: nx, cy: ny };
    }
  }
  return null;
}

/** Grid line-of-sight (for path smoothing). */
function los(ax: number, ay: number, bx: number, by: number): boolean {
  let x0 = ax, y0 = ay;
  const dx = Math.abs(bx - ax), dy = Math.abs(by - ay);
  const sx = ax < bx ? 1 : -1, sy = ay < by ? 1 : -1;
  let err = dx - dy;
  for (;;) {
    if (isBlocked(x0, y0)) return false;
    if (x0 === bx && y0 === by) return true;
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x0 += sx; }
    if (e2 < dx) { err += dx; y0 += sy; }
  }
}

/** A* (4-dir) + line-of-sight smoothing → world-coord waypoints (incl. `to`). */
export function findPath(from: { x: number; y: number }, to: { x: number; y: number }): Array<{ x: number; y: number }> {
  const s0 = cellOf(from), g0 = cellOf(to);
  const s = freeCellNear(s0.cx, s0.cy), g = freeCellNear(g0.cx, g0.cy);
  if (!s || !g) return [to];
  const start = s.cy * GRID_W + s.cx, goal = g.cy * GRID_W + g.cx;
  if (start === goal) return [to];

  const came = new Int32Array(GRID_W * GRID_H).fill(-1);
  const gScore = new Float64Array(GRID_W * GRID_H).fill(Infinity);
  gScore[start] = 0;
  // tiny binary-heap-free frontier — grid is ~3k cells, array scan is fine
  const open: number[] = [start];
  const h = (i: number) => Math.abs((i % GRID_W) - g.cx) + Math.abs(Math.floor(i / GRID_W) - g.cy);
  while (open.length) {
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (gScore[open[i]] + h(open[i]) < gScore[open[bi]] + h(open[bi])) bi = i;
    const cur = open.splice(bi, 1)[0];
    if (cur === goal) break;
    const cx = cur % GRID_W, cy = Math.floor(cur / GRID_W);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= GRID_W || ny >= GRID_H || isBlocked(nx, ny)) continue;
      const ni = ny * GRID_W + nx;
      const ng = gScore[cur] + 1;
      if (ng < gScore[ni]) {
        gScore[ni] = ng;
        came[ni] = cur;
        if (!open.includes(ni)) open.push(ni);
      }
    }
  }
  if (came[goal] === -1 && goal !== start) return [to]; // unreachable — walk straight (old behavior)

  const cells: Array<{ cx: number; cy: number }> = [];
  for (let i = goal; i !== -1; i = came[i]) cells.push({ cx: i % GRID_W, cy: Math.floor(i / GRID_W) });
  cells.reverse();
  // smooth: greedily jump to the furthest visible cell
  const pts: Array<{ x: number; y: number }> = [];
  let anchor = 0;
  while (anchor < cells.length - 1) {
    let far = anchor + 1;
    for (let j = cells.length - 1; j > anchor; j--) {
      if (los(cells[anchor].cx, cells[anchor].cy, cells[j].cx, cells[j].cy)) { far = j; break; }
    }
    pts.push({ x: cells[far].cx * CELL + CELL / 2, y: cells[far].cy * CELL + CELL / 2 });
    anchor = far;
  }
  pts[pts.length - 1] = { x: to.x, y: to.y }; // land exactly on the spot
  return pts;
}

/** Advance a point `dist` px along a waypoint path. Pure — used by the server
 *  tick and by clients extrapolating between snapshots. */
export function advanceAlong(
  x: number, y: number, path: Array<{ x: number; y: number }>, dist: number,
): { x: number; y: number; consumed: number } {
  let consumed = 0;
  for (let i = 0; i < path.length && dist > 0; ) {
    const t = path[i];
    const dx = t.x - x, dy = t.y - y;
    const seg = Math.hypot(dx, dy);
    if (seg <= dist) {
      x = t.x; y = t.y; dist -= seg; i++; consumed++;
    } else {
      x += (dx / seg) * dist; y += (dy / seg) * dist; dist = 0;
    }
  }
  return { x, y, consumed };
}

// ── Simulation ───────────────────────────────────────────────────────────────

export type OfficeMode = "working" | "visiting" | "meeting" | "observing" | "wandering" | "resting" | "leaving" | "away";

// Garden anchors where observed-chat clusters gather, and the window spots
// observers watch them from (offset per concurrent observer).
const CLUSTER_ANCHORS = [{ x: 1000, y: 430 }, { x: 1085, y: 225 }, { x: 955, y: 565 }];
const CLUSTER_OFFSETS = [{ x: -26, y: 0 }, { x: 26, y: 0 }, { x: 0, y: 30 }];
const OBSERVE_SPOT = { x: 652, y: 92 }; // the window nearest the garden

export interface SimEntity {
  name: string;
  x: number;
  y: number;
  face: 1 | -1;
  mode: OfficeMode;
  /** remaining waypoints (empty = arrived / stationary) */
  path: Array<{ x: number; y: number }>;
  /** action caption to apply when the walk finishes */
  pendingAction: string | null;
  dwellUntil: number;
  action: string | null;
  partner: string | null;
  desk: number | null;
  visible: boolean;
  speed: number;
}

/** An external contact whose message triggered a running spawn. */
export interface VisitorSpec {
  id: string;             // spawn id
  persona: string;
  name: string;           // sender display name, or the chat name for groups
  contact_id: number | null;
  platform: string;       // whatsapp | telegram | slack | gchat | email | …
  group: boolean;
  snippet: string | null; // what they asked
}

export interface VisitorState extends VisitorSpec {
  x: number;
  y: number;
  face: 1 | -1;
  path: Array<{ x: number; y: number }>;
  phase: "arriving" | "standing" | "leaving";
  speed: number;
}

/** An observed chat that's currently active — its people appear in the garden. */
export interface ObservedSpec {
  chat_id: string;
  title: string | null;
  platform: string;
  persona: string; // the observer
  participants: Array<{ id: number; name: string }>;
}

export interface GuestState {
  key: string; // chat_id|contact_id
  chat_id: string;
  name: string;
  contact_id: number;
  platform: string;
  x: number;
  y: number;
  face: 1 | -1;
  path: Array<{ x: number; y: number }>;
  phase: "arriving" | "standing" | "leaving";
  speed: number;
  action: string | null;
}

export interface SimInputs {
  roster: string[];
  /** personas with a running spawn right now */
  working: Set<string>;
  /** persona → ms epoch of most recent spawn start */
  lastSpawnAt: Map<string, number>;
  /** recent agent↔agent bus messages */
  pairs: Array<{ a: string; b: string; at: number }>;
  /** personas confined to the server room (bus isolated_agents) */
  isolated: Set<string>;
  /** contacts whose messages triggered the currently-running spawns */
  visitors: VisitorSpec[];
  /** active observed chats (≤3) — their people chat in the garden */
  observed: ObservedSpec[];
}

/** Behavior thresholds — the server overrides these from the Settings page. */
export interface SimConfig {
  wanderAgeMs: number;   // spawned within this → wandering
  restAgeMs: number;     // within this → resting; older → away
  interactMs: number;    // a bus message keeps a pair chatting this long
}
export const SIM_DEFAULTS: SimConfig = {
  wanderAgeMs: 60 * 60_000,
  restAgeMs: 12 * 3_600_000,
  interactMs: 90_000,
};

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function witaHour(now: number): number {
  return Number(new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Makassar", hour: "numeric", hour12: false }).format(new Date(now))) % 24;
}

export class OfficeSim {
  entities = new Map<string, SimEntity>();
  /** bumped on render-worthy changes (never on plain movement) */
  version = 0;
  private inputs: SimInputs = { roster: [], working: new Set(), lastSpawnAt: new Map(), pairs: [], isolated: new Set(), visitors: [], observed: [] };
  cfg: SimConfig = { ...SIM_DEFAULTS };
  visitors = new Map<string, VisitorState>();
  guests = new Map<string, GuestState>();
  weather: "clear" | "rain" = "clear";
  private weatherUntil = 0;
  cat: CatState = { x: 520, y: 420, face: 1, path: [], action: null, pending: null, speed: 30 };
  private catDwellUntil = 0;
  private deskOf = new Map<string, number>();
  /** last desk each persona used — re-offered when free, so seating is sticky
   *  and the office develops a recognizable geography */
  private deskPref = new Map<string, number>();
  private serverDeskOf = new Map<string, number>();
  private restOf = new Map<string, number>();
  private firstTick = true;
  private bumpNow = false;

  setInputs(inp: SimInputs) {
    this.inputs = inp;
  }

  /** Names allowed to rest this tick — the REST_SPOTS.length most-recently
   *  spawned of the rest-age candidates; sleepier ones go home instead. */
  private restAllowed = new Set<string>();

  private computeRestAllowed(now: number) {
    const inp = this.inputs;
    const candidates: Array<{ name: string; at: number }> = [];
    for (const name of inp.roster) {
      if (inp.working.has(name)) continue;
      const at = inp.lastSpawnAt.get(name);
      if (at === undefined) continue;
      const age = now - at;
      if (age >= this.cfg.wanderAgeMs && age < this.cfg.restAgeMs) candidates.push({ name, at });
    }
    candidates.sort((a, b) => b.at - a.at);
    this.restAllowed = new Set(candidates.slice(0, REST_SPOTS.length).map((c) => c.name));
  }

  private desired(name: string, now: number, hour: number): { mode: OfficeMode; partner: string | null } {
    const inp = this.inputs;
    if (inp.working.has(name)) return { mode: "working", partner: null };
    let best: { other: string; at: number } | null = null;
    for (const p of inp.pairs) {
      if (now - p.at > this.cfg.interactMs) continue;
      const other = p.a === name ? p.b : p.b === name ? p.a : null;
      if (!other || other === name || !this.entities.has(other)) continue;
      if (!best || p.at > best.at) best = { other, at: p.at };
    }
    if (best) return { mode: inp.working.has(best.other) ? "visiting" : "meeting", partner: best.other };
    // idle + one of my observed chats is active → watch it through the window
    if (inp.observed.some((o) => o.persona === name)) return { mode: "observing", partner: null };
    const at = inp.lastSpawnAt.get(name);
    if (at !== undefined && now - at < this.cfg.wanderAgeMs) return { mode: "wandering", partner: null };
    // morning arrival wave: 8–9 WITA everyone who'd merely nap is up and about
    if (at !== undefined && now - at < this.cfg.restAgeMs && hour === 8) return { mode: "wandering", partner: null };
    if (at !== undefined && now - at < this.cfg.restAgeMs && this.restAllowed.has(name)) return { mode: "resting", partner: null };
    return { mode: "away", partner: null };
  }

  private allocSlot(taken: Map<string, number>): number {
    const used = new Set(taken.values());
    let i = 0;
    while (used.has(i)) i++;
    return i;
  }

  private deskPos(slot: number): { x: number; y: number } {
    const base = DESKS[slot % DESKS.length];
    return { x: base.x + Math.floor(slot / DESKS.length) * 24, y: base.y };
  }

  private serverDeskPos(slot: number): { x: number; y: number } {
    const base = SERVER_DESKS[slot % SERVER_DESKS.length];
    return { x: base.x + Math.floor(slot / SERVER_DESKS.length) * 20, y: base.y };
  }

  private freeSlots(name: string) {
    this.deskOf.delete(name);
    this.serverDeskOf.delete(name);
    this.restOf.delete(name);
  }

  private walkTo(e: SimEntity, to: { x: number; y: number }) {
    e.path = findPath({ x: e.x, y: e.y }, to);
  }

  private tickHour = 12; // WITA hour, refreshed each tick

  private pickWanderPoi(e: SimEntity): POI {
    const hour = this.tickHour;
    if (this.inputs.isolated.has(e.name)) return SERVER_POIS[Math.floor(Math.random() * SERVER_POIS.length)];
    const raining = this.weather === "rain";
    // personal habit: every persona has a seeded favorite hangout
    const all = [...OFFICE_POIS, ...GARDEN_POIS];
    const favorite = all[hashStr(e.name) % all.length];
    if (Math.random() < 0.3 && !(raining && favorite.x > GARDEN_X - 40)) return favorite;
    // lunch rush 12–13 WITA
    if (hour === 12 && Math.random() < 0.7) return LUNCH_POIS[Math.floor(Math.random() * LUNCH_POIS.length)];
    const pool = !raining && Math.random() < 0.4 ? GARDEN_POIS : OFFICE_POIS;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /** Enter a new mode: allocate a spot, set the walk path. */
  private enter(e: SimEntity, mode: OfficeMode, partner: string | null) {
    const isolated = this.inputs.isolated.has(e.name);
    this.freeSlots(e.name);
    e.mode = mode;
    e.partner = partner;
    e.action = null;
    e.pendingAction = null;
    if (!e.visible && mode !== "away") {
      e.x = DOOR.x;
      e.y = DOOR.y;
      e.visible = true;
    }
    switch (mode) {
      case "working": {
        if (isolated) {
          const slot = this.allocSlot(this.serverDeskOf);
          this.serverDeskOf.set(e.name, slot);
          this.walkTo(e, this.serverDeskPos(slot));
        } else {
          const pref = this.deskPref.get(e.name);
          const used = new Set(this.deskOf.values());
          const slot = pref !== undefined && !used.has(pref) ? pref : this.allocSlot(this.deskOf);
          this.deskOf.set(e.name, slot);
          this.deskPref.set(e.name, slot);
          this.walkTo(e, this.deskPos(slot));
        }
        e.speed = 70;
        break;
      }
      case "visiting": {
        const partnerIsolated = partner ? this.inputs.isolated.has(partner) : false;
        if (partnerIsolated) {
          this.walkTo(e, SERVER_DOOR_SPOT); // can't enter the server room — knock
        } else {
          const pDesk = partner ? this.deskOf.get(partner) : undefined;
          const at = pDesk !== undefined ? this.deskPos(pDesk) : MEET_SPOTS[0];
          this.walkTo(e, { x: at.x + 46, y: at.y + 4 });
        }
        e.speed = 55;
        break;
      }
      case "meeting": {
        const eitherIsolated = isolated || (partner ? this.inputs.isolated.has(partner) : false);
        const key = [e.name, partner ?? ""].sort().join("|");
        const spot = eitherIsolated ? SERVER_DOOR_SPOT : MEET_SPOTS[hashStr(key) % MEET_SPOTS.length];
        const left = e.name < (partner ?? "");
        this.walkTo(e, { x: spot.x + (left ? -20 : 20), y: spot.y });
        e.speed = 55;
        break;
      }
      case "wandering": {
        const poi = this.pickWanderPoi(e);
        this.walkTo(e, { x: poi.x, y: poi.y });
        e.pendingAction = poi.action;
        e.speed = 32;
        break;
      }
      case "resting": {
        if (isolated) {
          this.walkTo(e, SERVER_REST);
        } else {
          const slot = this.allocSlot(this.restOf);
          if (slot < REST_SPOTS.length) {
            this.restOf.set(e.name, slot);
            this.walkTo(e, REST_SPOTS[slot]);
          } else {
            const poi = OFFICE_POIS[hashStr(e.name) % OFFICE_POIS.length];
            this.walkTo(e, { x: poi.x, y: poi.y });
          }
        }
        e.speed = 26;
        break;
      }
      case "observing": {
        const mine = this.inputs.observed.filter((o) => o.persona === e.name);
        const observers = [...new Set(this.inputs.observed.map((o) => o.persona))].sort();
        const idx = Math.max(0, observers.indexOf(e.name));
        this.walkTo(e, { x: OBSERVE_SPOT.x - idx * 40, y: OBSERVE_SPOT.y });
        const label = mine[0]?.title ?? mine[0]?.chat_id ?? "the chats";
        e.pendingAction = `👁 watching ${mine.length > 1 ? `${mine.length} chats` : label}`;
        e.speed = 45;
        break;
      }
      case "leaving": {
        this.walkTo(e, DOOR);
        e.speed = 40;
        break;
      }
      case "away": {
        e.path = [];
        e.visible = false;
        break;
      }
    }
    e.desk = this.deskOf.get(e.name) ?? null;
    this.bumpNow = true;
  }

  private onArrive(e: SimEntity, now: number) {
    switch (e.mode) {
      case "working":
        break;
      case "visiting":
      case "meeting": {
        const other = e.partner ? this.entities.get(e.partner) : null;
        if (other) e.face = other.x >= e.x ? 1 : -1;
        e.action = `💬 ${e.partner}`;
        break;
      }
      case "wandering":
        e.action = e.pendingAction;
        e.dwellUntil = now + 4000 + Math.random() * 7000;
        break;
      case "observing":
        e.action = e.pendingAction;
        e.face = 1;
        break;
      case "resting":
        e.action = "💤";
        break;
      case "leaving":
        e.visible = false;
        e.mode = "away";
        this.freeSlots(e.name);
        break;
      case "away":
        break;
    }
    this.bumpNow = true;
  }

  tick(now: number, dt: number) {
    this.bumpNow = false;
    const inp = this.inputs;
    this.tickHour = witaHour(now);
    this.computeRestAllowed(now);
    this.tickWeather(now);

    // roster sync
    const names = new Set(inp.roster);
    for (const name of names) {
      if (!this.entities.has(name)) {
        this.entities.set(name, {
          name, x: DOOR.x, y: DOOR.y, face: 1, mode: "away", path: [],
          pendingAction: null, dwellUntil: 0, action: null, partner: null,
          desk: null, visible: false, speed: 32,
        });
        this.bumpNow = true;
      }
    }
    for (const name of [...this.entities.keys()]) {
      if (!names.has(name)) {
        this.entities.delete(name);
        this.freeSlots(name);
        this.bumpNow = true;
      }
    }

    for (const e of this.entities.values()) {
      const want = this.desired(e.name, now, this.tickHour);
      const effective = e.mode === "leaving" ? "away" : e.mode;
      const partnerChanged = (want.mode === "meeting" || want.mode === "visiting") && want.partner !== e.partner;
      if (want.mode !== effective || partnerChanged) {
        if (want.mode === "away" && e.visible) {
          if (e.mode !== "leaving") this.enter(e, "leaving", null);
        } else if (want.mode === "away") {
          if (e.mode !== "away") this.enter(e, "away", null);
        } else {
          this.enter(e, want.mode, want.partner);
        }
      }

      // wander: pick the next destination once the dwell is over
      if (e.mode === "wandering" && e.path.length === 0 && now >= e.dwellUntil) {
        const poi = this.pickWanderPoi(e);
        this.walkTo(e, { x: poi.x, y: poi.y });
        e.pendingAction = poi.action;
        if (e.action) { e.action = null; this.bumpNow = true; }
      }

      // movement along the path
      if (e.path.length > 0) {
        if (this.firstTick) {
          const end = e.path[e.path.length - 1];
          e.x = end.x; e.y = end.y;
          e.path = [];
          this.onArrive(e, now);
        } else {
          const before = e.path[0];
          const moved = advanceAlong(e.x, e.y, e.path, e.speed * dt);
          if (Math.abs(moved.x - e.x) > 0.5) e.face = moved.x > e.x ? 1 : -1;
          else if (before && Math.abs(before.x - e.x) > 2) e.face = before.x > e.x ? 1 : -1;
          e.x = moved.x; e.y = moved.y;
          if (moved.consumed > 0) e.path = e.path.slice(moved.consumed);
          if (e.path.length === 0) this.onArrive(e, now);
        }
      }
    }

    this.tickVisitors(dt);
    this.tickGuests(now, dt);
    this.tickCat(now, dt);

    this.firstTick = false;
    if (this.bumpNow) this.version++;
  }

  /** Where a visitor stands: the customer side of their persona's desk. */
  private visitorSpot(persona: string, idx: number): { x: number; y: number } {
    const sDesk = this.serverDeskOf.get(persona);
    if (sDesk !== undefined) return { x: SERVER_DOOR_SPOT.x - 20 + idx * 30, y: SERVER_DOOR_SPOT.y }; // outside the glass
    const desk = this.deskOf.get(persona);
    const at = desk !== undefined ? this.deskPos(desk) : DOOR;
    return { x: at.x - 20 + idx * 30, y: at.y + 78 };
  }

  /** External contacts walk in for the spawns they triggered, stand at the
   *  desk while the persona works, and leave when the spawn ends. */
  private tickVisitors(dt: number) {
    const specs = new Map(this.inputs.visitors.map((v) => [v.id, v]));
    // arrivals
    const perPersona = new Map<string, number>();
    for (const [id, spec] of specs) {
      const idx = perPersona.get(spec.persona) ?? 0;
      perPersona.set(spec.persona, idx + 1);
      if (this.visitors.has(id)) continue;
      const v: VisitorState = {
        ...spec,
        x: DOOR.x, y: DOOR.y, face: 1,
        path: findPath(DOOR, this.visitorSpot(spec.persona, idx)),
        phase: "arriving", speed: 55,
      };
      this.visitors.set(id, v);
      this.bumpNow = true;
    }
    // departures + movement
    for (const [id, v] of [...this.visitors.entries()]) {
      if (!specs.has(id) && v.phase !== "leaving") {
        v.phase = "leaving";
        v.path = findPath({ x: v.x, y: v.y }, DOOR);
        this.bumpNow = true;
      }
      if (v.path.length > 0) {
        if (this.firstTick) {
          const end = v.path[v.path.length - 1];
          v.x = end.x; v.y = end.y; v.path = [];
        } else {
          const moved = advanceAlong(v.x, v.y, v.path, v.speed * dt);
          if (Math.abs(moved.x - v.x) > 0.5) v.face = moved.x > v.x ? 1 : -1;
          v.x = moved.x; v.y = moved.y;
          if (moved.consumed > 0) v.path = v.path.slice(moved.consumed);
        }
        if (v.path.length === 0) {
          if (v.phase === "leaving") { this.visitors.delete(id); }
          else if (v.phase === "arriving") {
            v.phase = "standing";
            const host = this.entities.get(v.persona);
            if (host) v.face = host.x >= v.x ? 1 : -1;
          }
          this.bumpNow = true;
        }
      }
    }
  }

  /** Random weather episodes. Rain sends garden wanderers (and the cat) inside. */
  private tickWeather(now: number) {
    if (now < this.weatherUntil) return;
    if (this.weather === "rain") {
      this.weather = "clear";
      this.weatherUntil = now + (20 + Math.random() * 40) * 60_000;
    } else if (Math.random() < 0.18) {
      this.weather = "rain";
      this.weatherUntil = now + (4 + Math.random() * 6) * 60_000;
      for (const e of this.entities.values()) {
        if (e.mode === "wandering" && e.x > GARDEN_X - 40) {
          const poi = OFFICE_POIS[Math.floor(Math.random() * OFFICE_POIS.length)];
          this.walkTo(e, { x: poi.x, y: poi.y });
          e.pendingAction = poi.action;
          e.action = null;
        }
      }
      if (this.cat.x > GARDEN_X - 40) { this.catDwellUntil = 0; this.cat.path = []; this.cat.action = null; }
    } else {
      this.weatherUntil = now + (10 + Math.random() * 30) * 60_000; // stay clear, re-roll later
      return;
    }
    this.bumpNow = true;
  }

  /** Cluster layout for the observed chats — shared with the client so labels
   *  land where the guests stand. */
  static clusterLayout(observed: ObservedSpec[]): Array<{ spec: ObservedSpec; anchor: { x: number; y: number } }> {
    return [...observed]
      .sort((a, b) => a.chat_id.localeCompare(b.chat_id))
      .slice(0, CLUSTER_ANCHORS.length)
      .map((spec, i) => ({ spec, anchor: CLUSTER_ANCHORS[i] }));
  }

  /** People from active observed chats hang out in the garden, chatting among
   *  themselves while their observer watches from the window. */
  private tickGuests(now: number, dt: number) {
    const want = new Map<string, { spec: ObservedSpec; name: string; contact_id: number; pos: { x: number; y: number }; face: 1 | -1 }>();
    for (const { spec, anchor } of OfficeSim.clusterLayout(this.inputs.observed)) {
      spec.participants.slice(0, CLUSTER_OFFSETS.length).forEach((p, pi) => {
        const off = CLUSTER_OFFSETS[pi];
        want.set(`${spec.chat_id}|${p.id}`, {
          spec, name: p.name, contact_id: p.id,
          pos: { x: anchor.x + off.x, y: anchor.y + off.y },
          face: off.x > 0 ? -1 : 1, // face the middle of the huddle
        });
      });
    }
    for (const [key, w] of want) {
      if (this.guests.has(key)) continue;
      const entryY = Math.min(STAGE.h - 30, w.pos.y + 40);
      this.guests.set(key, {
        key, chat_id: w.spec.chat_id, name: w.name, contact_id: w.contact_id, platform: w.spec.platform,
        x: STAGE.w + 24, y: entryY, face: -1,
        path: findPath({ x: STAGE.w - 16, y: entryY }, w.pos),
        phase: "arriving", speed: 40, action: null,
      });
      this.bumpNow = true;
    }
    for (const [key, g] of [...this.guests.entries()]) {
      if (!want.has(key) && g.phase !== "leaving") {
        g.phase = "leaving";
        g.action = null;
        g.path = [...findPath({ x: g.x, y: g.y }, { x: STAGE.w - 16, y: g.y }), { x: STAGE.w + 24, y: g.y }];
        this.bumpNow = true;
      }
      if (g.path.length > 0) {
        if (this.firstTick) {
          const end = g.path[g.path.length - 1];
          g.x = end.x; g.y = end.y; g.path = [];
        } else {
          const moved = advanceAlong(g.x, g.y, g.path, g.speed * dt);
          if (Math.abs(moved.x - g.x) > 0.5) g.face = moved.x > g.x ? 1 : -1;
          g.x = moved.x; g.y = moved.y;
          if (moved.consumed > 0) g.path = g.path.slice(moved.consumed);
        }
        if (g.path.length === 0) {
          if (g.phase === "leaving") this.guests.delete(key);
          else if (g.phase === "arriving") {
            g.phase = "standing";
            g.face = want.get(key)?.face ?? 1;
          }
          this.bumpNow = true;
        }
      }
    }
    // rotate the speaker in each huddle every few seconds
    const byChat = new Map<string, GuestState[]>();
    for (const g of this.guests.values()) {
      if (g.phase !== "standing") continue;
      const arr = byChat.get(g.chat_id) ?? [];
      arr.push(g);
      byChat.set(g.chat_id, arr);
    }
    for (const [chatId, gs] of byChat) {
      gs.sort((a, b) => a.key.localeCompare(b.key));
      const speaker = Math.floor(now / 3500 + hashStr(chatId)) % gs.length;
      gs.forEach((g, i) => {
        const a = i === speaker ? "💬" : null;
        if (g.action !== a) { g.action = a; this.bumpNow = true; }
      });
    }
  }

  /** The office cat. No pathfinding — cats walk over desks. */
  private tickCat(now: number, dt: number) {
    const c = this.cat;
    if (c.path.length === 0 && now >= this.catDwellUntil) {
      const raining = this.weather === "rain";
      const spots = raining ? CAT_SPOTS.filter((s) => s.x < GARDEN_X - 40) : CAT_SPOTS;
      const spot = spots[Math.floor(Math.random() * spots.length)];
      c.path = [{ x: spot.x, y: spot.y }];
      c.speed = Math.random() < 0.08 ? 110 : 24 + Math.random() * 18; // occasional zoomies
      if (c.action) { c.action = null; this.bumpNow = true; }
      c.pending = spot.action;
    }
    if (c.path.length > 0) {
      if (this.firstTick) {
        const end = c.path[c.path.length - 1];
        c.x = end.x; c.y = end.y; c.path = [];
      } else {
        const moved = advanceAlong(c.x, c.y, c.path, c.speed * dt);
        if (Math.abs(moved.x - c.x) > 0.5) c.face = moved.x > c.x ? 1 : -1;
        c.x = moved.x; c.y = moved.y;
        if (moved.consumed > 0) c.path = c.path.slice(moved.consumed);
      }
      if (c.path.length === 0) {
        const napping = c.pending === "💤" || Math.random() < 0.3;
        c.action = napping ? "💤" : c.pending;
        this.catDwellUntil = now + (napping ? 40_000 + Math.random() * 80_000 : 8_000 + Math.random() * 25_000);
        this.bumpNow = true;
      }
    }
  }
}
